/**
 * @engine/vfx - Update Context Nodes
 *
 * Nodes for updating particles each frame.
 */

import type { VFXNodeDefinition } from '../../types';
import { dataPort, flowPort } from '../../types';

/**
 * Update context node definitions.
 */

export const SETTER_NODES: readonly VFXNodeDefinition[] = [
  // ---- Set Position ----
  {
    id: 'vfx.update.set_position',
    name: 'Set Position',
    category: 'attribute',
    contexts: ['update'],
    description: 'Set particle position directly',
    color: '#FF9800',
    inputs: [
      flowPort('exec', ''),
      dataPort('position', 'Position', 'vec3', { defaultValue: { x: 0, y: 0, z: 0 } }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `particle.position = position;`,
    },
  },

  // ---- Add Position ----
  {
    id: 'vfx.update.add_position',
    name: 'Add Position',
    category: 'attribute',
    contexts: ['update'],
    description: 'Add offset to particle position',
    color: '#FF9800',
    inputs: [
      flowPort('exec', ''),
      dataPort('offset', 'Offset', 'vec3', { defaultValue: { x: 0, y: 0, z: 0 } }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `particle.position += offset;`,
    },
  },

  // ---- Set Velocity ----
  {
    id: 'vfx.update.set_velocity',
    name: 'Set Velocity',
    category: 'attribute',
    contexts: ['update'],
    description: 'Set particle velocity directly',
    color: '#FF9800',
    inputs: [
      flowPort('exec', ''),
      dataPort('velocity', 'Velocity', 'vec3', { defaultValue: { x: 0, y: 0, z: 0 } }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `particle.velocity = velocity;`,
    },
  },

  // ---- Add Velocity ----
  {
    id: 'vfx.update.add_velocity',
    name: 'Add Velocity',
    category: 'attribute',
    contexts: ['update'],
    description: 'Add to particle velocity',
    color: '#FF9800',
    inputs: [
      flowPort('exec', ''),
      dataPort('force', 'Force', 'vec3', { defaultValue: { x: 0, y: 0, z: 0 } }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `particle.velocity += force;`,
    },
  },

  // ---- Set Color ----
  {
    id: 'vfx.update.set_color',
    name: 'Set Color',
    category: 'attribute',
    contexts: ['update'],
    description: 'Set particle color directly',
    color: '#FF9800',
    inputs: [
      flowPort('exec', ''),
      dataPort('color', 'Color', 'color', { defaultValue: { r: 1, g: 1, b: 1, a: 1 } }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `particle.color = color;`,
    },
  },

  // ---- Set Size ----
  {
    id: 'vfx.update.set_size',
    name: 'Set Size',
    category: 'attribute',
    contexts: ['update'],
    description: 'Set particle size directly',
    color: '#FF9800',
    inputs: [
      flowPort('exec', ''),
      dataPort('size', 'Size', 'float', { defaultValue: 1, range: { min: 0, max: 100 } }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `particle.size = size;`,
    },
  },

  // ---- Update Rotation ----
  {
    id: 'vfx.update.rotation',
    name: 'Update Rotation',
    category: 'attribute',
    contexts: ['update'],
    description: 'Apply angular velocity to rotation',
    color: '#FF9800',
    inputs: [
      flowPort('exec', ''),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `particle.rotation += particle.angularVelocity * deltaTime;`,
    },
  },
];
